import {
  Dialog,
  DialogContent,
  ThemeProvider,
  Box,
  Button,
  Typography,
} from "@mui/material";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import generateRewampTheme from "../rewamp/theme";
import { AppLayoutContext } from "../contexts/AppLayoutContext";

// Always rendered inside the rewamp theme — shared between rewamp call sites
// and legacy-themed ones (AppBar, DataCleanupModule, AssetsDetailPage).
function ActionConfirmationDialog({
  open,
  setOpen,
  onContinueClick,
  isDelete = false,
  actionHeader,
  warningText,
  continueLabel,
  width = "516px",
  borderRadius = "16px",
  contentGap = 3,
}) {
  const { t } = useTranslation();
  const { fontSize, ourTheme } = useContext(AppLayoutContext);
  const rewampTheme = useMemo(
    () => generateRewampTheme(fontSize, ourTheme),
    [fontSize, ourTheme],
  );

  const handleClose = () => setOpen(false);
  const titleId = "action-confirmation-title";
  const descId = "action-confirmation-desc";

  return (
    <ThemeProvider theme={rewampTheme}>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby={actionHeader ? titleId : descId}
        aria-describedby={descId}
        PaperProps={{
          sx: {
            width,
            maxWidth: "calc(100% - 32px)",
            borderRadius,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: (theme) => theme.palette.layout.cardShadow,
            p: 4,
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: contentGap,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
            }}
          >
            {actionHeader && (
              <Typography
                id={titleId}
                variant="dialogTitle"
                component="h2"
                sx={{
                  color: isDelete ? "text.error" : "neutral.headings",
                  textAlign: "center",
                }}
              >
                {t(actionHeader)}
              </Typography>
            )}
            <Typography
              id={descId}
              variant="body1"
              component="p"
              sx={{
                color: "formText.checkbox",
                textAlign: "center",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "30px",
              }}
            >
              {t(warningText)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
            <Button variant="secondary" size="action" onClick={handleClose} sx={{ flex: 1 }}>
              {t("COMMON_CANCEL")}
            </Button>
            <Button
              variant="primary"
              size="action"
              onClick={onContinueClick}
              autoFocus
              sx={{ flex: 1 }}
            >
              {continueLabel
                ? t(continueLabel)
                : isDelete
                  ? t("COMMON_YES_DELETE")
                  : t("COMMON_YES_LOGOUT")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}

ActionConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
  isDelete: PropTypes.bool,
  /** Omit to render without a title — just the centered warningText. */
  actionHeader: PropTypes.string,
  warningText: PropTypes.string.isRequired,
  /** i18n key for the primary button; defaults to the delete/logout pair via isDelete. */
  continueLabel: PropTypes.string,
  width: PropTypes.string,
  borderRadius: PropTypes.string,
  contentGap: PropTypes.number,
};

export default ActionConfirmationDialog;
